
import { Component, OnInit } from '@angular/core';

import { AuthorizedUsersDialogComponent } from '@components/authorized-users-dialog/authorized-users-dialog.component';

import { Roles, Sectors, TypeContracts, } from '@models/users/role.const';

import { SubscriberDto } from '@models/users/subscriber.dto';

import { AccountService } from '@services/account.service';
import { MessageService } from '@services/message.service';
import { SubscriberService } from '@services/subscriber.service';

import { MenuItem, PrimeIcons } from 'primeng/api';

import { finalize } from 'rxjs';

import {
  DialogService,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';

import { DelegationDialogComponent } from './delegation-dialog/delegation-dialog.component';

/*
 * Filtri disponibili per i contatori.
 * null indica che nessun filtro è attivo.
 */
type SubscriberFilter =
  | 'internal'
  | 'external'
  | 'AT'
  | 'EX-COPIT'
  | null;

@Component({
  selector: 'ebs-subscribers',
  templateUrl: './subscribers.page.html',
  styleUrls: ['./subscribers.page.scss'],
})
export class SubscribersPage implements OnInit {

  public users: SubscriberDto[] = [];

  public search: string = '';

  /*
   * Filtro attivo sui contatori.
   * Può essere selezionato un filtro alla volta.
   */
  public activeFilter: SubscriberFilter = null;

  public currentlyEditing: SubscriberDto | null = null;

  public subscriberDialogVisible = false;

  public selectedSubscriber: SubscriberDto | null = null;

  // Copia originale utilizzata per annullare le modifiche.
  private originalSubscriber: SubscriberDto | null = null;

  public isCreatingSubscriber = false;

  // La dialog può essere aperta in sola lettura.
  public isEditingSubscriber = false;

  public savingSubscriber = false;

  /*
   * Stato della dialog di scelta
   * dell'esportazione Excel.
   */
  public exportDialogVisible = false;

  public usersLoading = false;

  constructor(
    private subscriberService: SubscriberService,
    public dialogService: DialogService,
    private accountService: AccountService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.loadAll();
  }

  /* =====================================================
     PERMESSI
     ===================================================== */

  public get hasModifyPermissions(): boolean {
    return (
      this.accountService.group!.authorizations.filter(
        (elem) =>
          [
            'subscriberCreate',
            'subscriberModify',
            'subscriberRemoval',
            'externalCodeGeneration',
            'sendCodes',
          ].includes(elem.functionality.name),
      ).length > 0
    );
  }

  /* =====================================================
     CONTATORI
     ===================================================== */

  public get external(): number {
    return this.users.filter(
      (elem) => elem.isExternal,
    ).length;
  }

  public get internal(): number {
    return this.users.filter(
      (elem) => !elem.isExternal,
    ).length;
  }

  public get contractAT(): number {
    return this.users.filter(
      (elem) => elem.typeContract === 'AT',
    ).length;
  }

  public get contratEX(): number {
    return this.users.filter(
      (elem) => elem.typeContract === 'EX-COPIT',
    ).length;
  }

  /* =====================================================
     FILTRI CONTATORI
     ===================================================== */

  /**
   * Attiva il filtro selezionato.
   *
   * Se viene cliccato nuovamente il filtro
   * già attivo, lo disattiva.
   */
  public toggleFilter(
    filter: Exclude<SubscriberFilter, null>,
  ): void {
    this.activeFilter =
      this.activeFilter === filter
        ? null
        : filter;
  }

  /**
   * Rimuove il filtro dei contatori.
   * Non cancella la ricerca testuale.
   */
  public clearFilter(): void {
    this.activeFilter = null;
  }

  /**
   * Etichetta del filtro attivo.
   */
  public get activeFilterLabel(): string {
    switch (this.activeFilter) {
      case 'internal':
        return 'Interni';

      case 'external':
        return 'Esterni';

      case 'AT':
        return 'Contratto AT';

      case 'EX-COPIT':
        return 'EX-COPIT';

      default:
        return '';
    }
  }

  /* =====================================================
     OPZIONI
     ===================================================== */

  public get roles() {
    return Roles;
  }

  public get sectors(): string[] {
    return Sectors;
  }

  public get typeContracts(): string[] {
    return TypeContracts;
  }

  /* =====================================================
     FILTRO RICERCA
     ===================================================== */

  /**
   * Applica il filtro dei contatori
   * e successivamente la ricerca testuale.
   */
  public get filteredUsers(): SubscriberDto[] {
    let filtered = this.users;

    switch (this.activeFilter) {
      case 'internal':
        filtered = filtered.filter(
          (subscriber) => !subscriber.isExternal,
        );
        break;

      case 'external':
        filtered = filtered.filter(
          (subscriber) => subscriber.isExternal,
        );
        break;

      case 'AT':
        filtered = filtered.filter(
          (subscriber) =>
            subscriber.typeContract === 'AT',
        );
        break;

      case 'EX-COPIT':
        filtered = filtered.filter(
          (subscriber) =>
            subscriber.typeContract === 'EX-COPIT',
        );
        break;
    }

    const value = this.search.trim().toLowerCase();

    if (!value) {
      return filtered;
    }

    return filtered.filter((subscriber) => {
      const fields = [
        subscriber.name,
        subscriber.lastName,
        subscriber.registerNumber,
        subscriber.email,
        subscriber.phone,
        subscriber.role,
        subscriber.headquarter,
        subscriber.sector,
      ];

      return fields.some((field) =>
        String(field || '')
          .toLowerCase()
          .includes(value),
      );
    });
  }

  public trackBySubscriber(
    index: number,
    subscriber: SubscriberDto,
  ): number | string {
    return subscriber.subscriberId != null
      ? subscriber.subscriberId
      : index;
  }

  /* =====================================================
     CARICAMENTO
     ===================================================== */

  loadAll(): void {
    this.loadUsers();
  }

  loadUsers(): void {
    this.usersLoading = true;

    this.subscriberService
      .getSubscribers()
      .pipe(
        finalize(() => {
          this.usersLoading = false;
        }),
      )
      .subscribe({
        next: (users) => {
          this.users = users;
        },
        error: () => {
          this.messageService.error(
            'Caricamento iscritti',
            'Errore durante il caricamento degli iscritti',
          );
        },
      });
  }

  /* =====================================================
     APERTURA DIALOG ISCRITTO
     ===================================================== */

  openSubscriber(
    subscriber: SubscriberDto,
  ): void {
    this.isCreatingSubscriber = false;

    // La dialog si apre in sola lettura.
    this.isEditingSubscriber = false;

    // Salviamo una copia originale.
    this.originalSubscriber = {
      ...subscriber,
    };

    // La dialog lavora su una copia separata.
    this.selectedSubscriber = {
      ...subscriber,
    };

    this.subscriberDialogVisible = true;
  }

  createNewSubscriber(): void {
    if (!this.hasModifyPermissions) {
      return;
    }

    this.isCreatingSubscriber = true;

    // La creazione richiede i campi modificabili.
    this.isEditingSubscriber = true;

    this.originalSubscriber = null;

    this.selectedSubscriber = {
      name: '',
      lastName: '',
      email: '',
      phone: '',
      externalCode: this.randomString(
        6,
        '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ',
      ),
      headquarter: 'Pistoia',
      registerNumber: '',
      role: '',
      isExternal: false,
      sector: '',
      typeContract: '',
    };

    this.subscriberDialogVisible = true;
  }

  /* =====================================================
     ABILITA / ANNULLA MODIFICA
     ===================================================== */

  enableSubscriberEditing(): void {
    if (
      !this.selectedSubscriber ||
      !this.hasModifyPermissions
    ) {
      return;
    }

    this.isEditingSubscriber = true;
  }

  cancelSubscriberEditing(): void {
    if (this.savingSubscriber) {
      return;
    }

    if (this.originalSubscriber) {
      this.selectedSubscriber = {
        ...this.originalSubscriber,
      };
    }

    this.isEditingSubscriber = false;
  }

  /* =====================================================
     CHIUSURA DIALOG ISCRITTO
     ===================================================== */

  closeSubscriberDialog(): void {
    if (this.savingSubscriber) {
      return;
    }

    this.subscriberDialogVisible = false;

    this.selectedSubscriber = null;
    this.originalSubscriber = null;

    this.isCreatingSubscriber = false;
    this.isEditingSubscriber = false;
  }

  /* =====================================================
     SALVATAGGIO
     ===================================================== */

  saveDialogSubscriber(): void {
    if (
      !this.selectedSubscriber ||
      !this.hasModifyPermissions ||
      !this.isEditingSubscriber ||
      this.savingSubscriber
    ) {
      return;
    }

    const subscriber = {
      ...this.selectedSubscriber,
    };

    if (
      !subscriber.name.trim() ||
      !subscriber.lastName.trim()
    ) {
      this.messageService.error(
        'Dati mancanti',
        'Inserisci nome e cognome',
      );
      return;
    }

    this.savingSubscriber = true;

    this.subscriberService
      .modifySubscriber([subscriber])
      .subscribe({
        next: () => {
          this.savingSubscriber = false;

          this.messageService.success(
            this.isCreatingSubscriber
              ? 'Creazione iscritto'
              : 'Modifica iscritto',
            this.isCreatingSubscriber
              ? 'Creazione avvenuta con successo'
              : 'Modifica avvenuta con successo',
          );

          this.closeSubscriberDialog();
          this.loadUsers();
        },
        error: () => {
          this.savingSubscriber = false;

          this.messageService.error(
            'Salvataggio iscritto',
            'Errore durante il salvataggio',
          );
        },
      });
  }

  /* =====================================================
     ELIMINAZIONE
     ===================================================== */

  deleteSelectedSubscriber(): void {
    if (
      !this.selectedSubscriber ||
      this.isCreatingSubscriber ||
      !this.hasModifyPermissions ||
      this.savingSubscriber
    ) {
      return;
    }

    const subscriber = {
      ...this.selectedSubscriber,
    };

    const confirmed = window.confirm(
      `Vuoi eliminare l'iscritto ${subscriber.name} ${subscriber.lastName}?`,
    );

    if (!confirmed) {
      return;
    }

    this.savingSubscriber = true;

    this.subscriberService
      .removeSubscriber([subscriber])
      .subscribe({
        next: () => {
          this.savingSubscriber = false;

          this.messageService.success(
            'Rimozione iscritto',
            'Rimozione avvenuta con successo',
          );

          this.closeSubscriberDialog();
          this.loadUsers();
        },
        error: () => {
          this.savingSubscriber = false;

          this.messageService.error(
            'Rimozione iscritto',
            'Errore durante la rimozione',
          );
        },
      });
  }

  /* =====================================================
     GENERAZIONE CODICI
     ===================================================== */

  randomString(
    length: number,
    chars: string,
  ): string {
    let result = '';

    for (let i = length; i > 0; --i) {
      const index = Math.floor(
        Math.random() * chars.length,
      );

      result += chars[index];
    }

    return result;
  }

  regenerateDialogCode(): void {
    if (
      !this.selectedSubscriber ||
      !this.hasModifyPermissions ||
      !this.isEditingSubscriber
    ) {
      return;
    }

    this.selectedSubscriber.externalCode =
      this.randomString(
        6,
        '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ',
      );
  }

  generateNewSingleCode(
    user: SubscriberDto,
  ): void {
    const updatedUser = {
      ...user,
      externalCode: this.randomString(
        6,
        '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ',
      ),
    };

    this.subscriberService
      .modifySubscriber([updatedUser])
      .subscribe({
        next: () => {
          this.messageService.success(
            'Modifica iscritto',
            'Codice aggiornato con successo',
          );

          this.loadUsers();
        },
        error: () => {
          this.messageService.error(
            'Modifica iscritto',
            'Errore durante la modifica',
          );
        },
      });
  }

  generateNewCode(): void {
    const updatedUsers = this.users.map(
      (subscriber) => ({
        ...subscriber,
        externalCode: this.randomString(
          6,
          '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ',
        ),
      }),
    );

    this.subscriberService
      .modifySubscriber(updatedUsers)
      .subscribe({
        next: () => {
          this.messageService.success(
            'Modifica iscritti',
            'Codici aggiornati con successo',
          );

          this.loadUsers();
        },
        error: () => {
          this.messageService.error(
            'Modifica iscritti',
            'Errore durante la modifica',
          );
        },
      });
  }

  /* =====================================================
     MENU RAPIDO
     ===================================================== */

  public get items(): MenuItem[] {
    return this.hasModifyPermissions
      ? [
          {
            command: () =>
              this.openDelegationDialog(),
            icon: PrimeIcons.USER_PLUS,
          },
          {
            command: () =>
              this.openExportDialog(),
            icon: PrimeIcons.FILE_EXCEL,
          },
          {
            command: () =>
              this.generateNewCode(),
            icon: PrimeIcons.REFRESH,
          },
          {
            command: () =>
              this.sendEmails(),
            icon: PrimeIcons.ENVELOPE,
          },
        ]
      : [
          {
            command: () =>
              this.openDelegationDialog(),
            icon: PrimeIcons.USER_PLUS,
          },
        ];
  }

  /* =====================================================
     DELEGHE
     ===================================================== */

  openDelegationDialog(): void {
    const dialogRef: DynamicDialogRef =
      this.dialogService.open(
        DelegationDialogComponent,
        {
          maximizable: false,
          draggable: false,
          height: '100%',
          width: '100%',
          closable: false,
          closeOnEscape: false,
        },
      );

    dialogRef.onClose.subscribe(
      (result: boolean) => {
        if (result) {
          this.loadUsers();
        }
      },
    );
  }

  /* =====================================================
     INVIO EMAIL
     ===================================================== */

  sendEmails(): void {
    this.accountService
      .getAuthList()
      .subscribe((allUsers) => {
        const dialogRef: DynamicDialogRef =
          this.dialogService.open(
            AuthorizedUsersDialogComponent,
            {
              maximizable: false,
              draggable: false,
              height: '100%',
              width: '100%',
              closable: true,
              closeOnEscape: false,
              data: {
                selectedUsers: [],
                allUsers,
                allowSimplifiedView: true,
              },
            },
          );

        dialogRef.onClose.subscribe(
          (authorized: any[]) => {
            if (!authorized) {
              return;
            }

            this.subscriberService
              .sendGeneratedCodeEmails(authorized)
              .subscribe(() => {
                this.messageService.success(
                  'Invio mail',
                  'Invio iniziato con successo',
                );
              });
          },
        );
      });
  }

  /* =====================================================
     ESPORTAZIONE EXCEL - DIALOG DI SCELTA
     ===================================================== */

  /**
   * Mantiene il nome del metodo precedente,
   * ma ora apre la dialog invece di scaricare
   * immediatamente il file Excel.
   */
  exportToExcel(): void {
    this.openExportDialog();
  }

  /**
   * Apre la dialog con le due tipologie
   * di esportazione.
   */
  openExportDialog(): void {
    this.exportDialogVisible = true;
  }

  /**
   * Chiude la dialog di esportazione.
   */
  closeExportDialog(): void {
    this.exportDialogVisible = false;
  }

  /* =====================================================
     ESPORTAZIONE 1 - CONSEGNA STRENNE
     ===================================================== */

  /**
   * Esporta tutti gli iscritti con sei colonne:
   *
   * registerNumber
   * name
   * surname (valore di lastName)
   * externalCode
   * sector
   * headquarter
   *
   * Ordinamento:
   * 1. Città / headquarter
   * 2. Settore / sector
   * 3. Ruolo / role
   * 4. Cognome alfabetico
   * 5. Nome alfabetico
   *
   * L'esportazione utilizza tutti gli iscritti,
   * indipendentemente dai filtri della pagina.
   */
  exportConsegnaStrenne(): void {
    const orderedUsers = [...this.users].sort(
      (a, b) => {

        // 1. Ordinamento per città.
        const headquarterComparison =
          this.compareItalianText(
            a.headquarter,
            b.headquarter,
          );

        if (headquarterComparison !== 0) {
          return headquarterComparison;
        }

        // 2. Ordinamento per settore.
        const sectorComparison =
          this.compareItalianText(
            a.sector,
            b.sector,
          );

        if (sectorComparison !== 0) {
          return sectorComparison;
        }

        // 3. Ordinamento per ruolo.
        const roleComparison =
          this.compareItalianText(
            a.role,
            b.role,
          );

        if (roleComparison !== 0) {
          return roleComparison;
        }

        // 4. Ordinamento per cognome.
        const surnameComparison =
          this.compareItalianText(
            a.lastName,
            b.lastName,
          );

        if (surnameComparison !== 0) {
          return surnameComparison;
        }

        // 5. Ordinamento per nome.
        return this.compareItalianText(
          a.name,
          b.name,
        );
      },
    );

    const rows = orderedUsers.map(
      (subscriber) => ({
        registerNumber:
          subscriber.registerNumber ?? '',
        name:
          subscriber.name ?? '',
        surname:
          subscriber.lastName ?? '',
        externalCode:
          subscriber.externalCode ?? '',
        sector:
          subscriber.sector ?? '',
        headquarter:
          subscriber.headquarter ?? '',
      }),
    );

    this.createExcelFile(
      rows,
      [
        'registerNumber',
        'name',
        'surname',
        'externalCode',
        'sector',
        'headquarter',
      ],
      'Consegna_strenne',
      'Consegna strenne',
    );
  }

  /* =====================================================
     ESPORTAZIONE 2 - LISTA CONVENZIONI
     ===================================================== */

  /**
   * Esporta tutti gli iscritti ad eccezione
   * di quelli con ruolo "Convenzione".
   *
   * Colonne:
   * registerNumber
   * name
   * surname (valore di lastName)
   * externalCode
   *
   * Ordinamento alfabetico per cognome
   * e successivamente per nome.
   *
   * Il confronto sul ruolo ignora
   * maiuscole, minuscole e spazi.
   */
  exportListaConvenzioni(): void {
    const orderedUsers = this.users
      .filter((subscriber) => {
        const role = String(
          subscriber.role ?? '',
        )
          .trim()
          .toLocaleLowerCase('it');

        return role !== 'convenzione';
      })
      .sort((a, b) => {
        const surnameComparison =
          this.compareItalianText(
            a.lastName,
            b.lastName,
          );

        if (surnameComparison !== 0) {
          return surnameComparison;
        }

        return this.compareItalianText(
          a.name,
          b.name,
        );
      });

    const rows = orderedUsers.map(
      (subscriber) => ({
        registerNumber:
          subscriber.registerNumber ?? '',
        name:
          subscriber.name ?? '',
        surname:
          subscriber.lastName ?? '',
        externalCode:
          subscriber.externalCode ?? '',
      }),
    );

    this.createExcelFile(
      rows,
      [
        'registerNumber',
        'name',
        'surname',
        'externalCode',
      ],
      'Lista_convenzioni',
      'Lista convenzioni',
    );
  }

  /* =====================================================
     FUNZIONI DI SUPPORTO EXCEL
     ===================================================== */

  /**
   * Confronta due testi utilizzando
   * l'ordinamento alfabetico italiano.
   *
   * Ignora differenze tra maiuscole
   * e minuscole.
   */
  private compareItalianText(
    first: unknown,
    second: unknown,
  ): number {
    const firstValue = String(
      first ?? '',
    ).trim();

    const secondValue = String(
      second ?? '',
    ).trim();

    return firstValue.localeCompare(
      secondValue,
      'it',
      {
        sensitivity: 'base',
        numeric: true,
      },
    );
  }

  /**
   * Genera un file Excel con:
   *
   * RIGA 1:
   * Data e ora di esportazione.
   *
   * RIGA 2:
   * Intestazioni delle colonne.
   *
   * RIGA 3 e successive:
   * Dati degli iscritti.
   *
   * Imposta inoltre larghezze adeguate
   * e il filtro Excel sulle intestazioni.
   */
  private createExcelFile(
    rows: Record<string, unknown>[],
    columns: string[],
    fileName: string,
    sheetName: string,
  ): void {
    import('xlsx')
      .then((xlsx) => {

        /*
         * Data e ora locali del dispositivo
         * da cui viene eseguita l'esportazione.
         */
        const exportDate = new Date();

        const formattedDate =
          exportDate.toLocaleString(
            'it-IT',
            {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false,
            },
          );

        const exportInfo =
          `Data e ora esportazione: ${formattedDate}`;

        /*
         * Prima riga: data e ora.
         * Seconda riga: intestazioni.
         */
        const worksheet =
          xlsx.utils.aoa_to_sheet([
            [exportInfo],
            columns,
          ]);

        /*
         * I dati vengono inseriti dalla
         * terza riga, senza ripetere
         * le intestazioni.
         */
        xlsx.utils.sheet_add_json(
          worksheet,
          rows,
          {
            header: columns,
            skipHeader: true,
            origin: 'A3',
          },
        );

        /*
         * Unisce le celle della prima riga
         * per visualizzare meglio la data
         * e l'ora di esportazione.
         */
        worksheet['!merges'] = [
          {
            s: {
              r: 0,
              c: 0,
            },
            e: {
              r: 0,
              c: columns.length - 1,
            },
          },
        ];

        /*
         * Calcola automaticamente
         * la larghezza delle colonne.
         */
        worksheet['!cols'] = columns.map(
          (column) => {
            const maxContentLength = rows.reduce(
              (maxLength, row) => {
                const value = String(
                  row[column] ?? '',
                );

                return Math.max(
                  maxLength,
                  value.length,
                );
              },
              column.length,
            );

            return {
              wch: Math.min(
                Math.max(
                  maxContentLength + 3,
                  14,
                ),
                40,
              ),
            };
          },
        );

        /*
         * Filtro Excel sulla seconda riga,
         * dove si trovano le intestazioni.
         */
        const lastColumn =
          xlsx.utils.encode_col(
            columns.length - 1,
          );

        const lastRow = Math.max(
          rows.length + 2,
          2,
        );

        worksheet['!autofilter'] = {
          ref: `A2:${lastColumn}${lastRow}`,
        };

        /*
         * Creazione workbook.
         */
        const workbook =
          xlsx.utils.book_new();

        xlsx.utils.book_append_sheet(
          workbook,
          worksheet,
          sheetName,
        );

        /*
         * Generazione contenuto Excel.
         */
        const excelBuffer = xlsx.write(
          workbook,
          {
            bookType: 'xlsx',
            type: 'array',
          },
        );

        this.saveAsExcelFile(
          excelBuffer,
          fileName,
        );

        this.closeExportDialog();
      })
      .catch(() => {
        this.messageService.error(
          'Esportazione Excel',
          'Errore durante la creazione del file Excel',
        );
      });
  }

  /**
   * Salva il file Excel sul dispositivo.
   */
  saveAsExcelFile(
    buffer: any,
    fileName: string,
  ): void {
    const EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

    const EXCEL_EXTENSION = '.xlsx';

    const data: Blob = new Blob(
      [buffer],
      {
        type: EXCEL_TYPE,
      },
    );

    const name =
      fileName +
      '_export' +
      EXCEL_EXTENSION;

    const downloadLink =
      document.createElement('a');

    const url =
      window.URL.createObjectURL(data);

    downloadLink.href = url;

    downloadLink.setAttribute(
      'download',
      name,
    );

    document.body.appendChild(
      downloadLink,
    );

    downloadLink.click();

    document.body.removeChild(
      downloadLink,
    );

    window.URL.revokeObjectURL(url);
  }
}
